/* eslint-disable @typescript-eslint/no-explicit-any */
import * as protoutil from '../utils/protoutil';
import { MutationSource } from '../constants/mutation-constants';
import {
    type IObservableDataModel,
    type WithDataModelChangeNotifier,
    ChangeType,
} from '../model/observable-data-model';
import { DataType } from '../data';
import { PresentationEventTypes } from './event-manager/event-types';
import type { IEventManager } from './event-manager';
import type { IDataModel } from '../model/data-model';
import type { RawDataModelChange } from '../model/observable-data-model';
import type { TableDataModel } from '../model/table-data-model';

type ChangeItem = {
    key: string;
    model: IDataModel & IObservableDataModel<unknown, unknown> & WithDataModelChangeNotifier<unknown, unknown>;
    source: MutationSource;
    change: RawDataModelChange;
    ignore: boolean;
};

export class ObserverManager {
    private freshModelIdsSet = new Set<string>();
    private detachedModelIdsSet = new Set<string>();
    private changeItems: ChangeItem[] = [];
    private source: MutationSource = MutationSource.None;

    private isInTransaction = false;

    public constructor(private events: IEventManager) {
        this.init();
    }

    /**
     * NOTE: 需要确保在 MutationManager 之后初始化，否则变更通知会丢失
     */
    private init() {
        this.events.on(PresentationEventTypes.MutationSource, (source) => {
            this.source = source;
        });

        this.events.on(PresentationEventTypes.EnqueueChange, (model, change) => {
            this.enqueueChange({
                key: this.getChangeKey(model, change, this.source),
                model,
                source: this.source,
                change,
                ignore: false,
            });
        });

        this.events.on(PresentationEventTypes.AttachDataModel, (model) => {
            if (!this.isInTransaction) return;

            if (this.detachedModelIdsSet.has(model.id)) {
                this.detachedModelIdsSet.delete(model.id);
            } else {
                this.freshModelIdsSet.add(model.id);
            }
        });

        this.events.on(PresentationEventTypes.DetachDataModel, (model) => {
            if (!this.isInTransaction) return;

            if (this.freshModelIdsSet.has(model.id)) {
                this.freshModelIdsSet.delete(model.id);
            } else {
                this.detachedModelIdsSet.add(model.id);
            }
        });

        this.events.on(PresentationEventTypes.BeginTransaction, (depth) => {
            if (depth === 0) {
                this.reset();
                this.isInTransaction = true;
            }
        });

        this.events.on(PresentationEventTypes.CommitTransaction, () => {
            this.isInTransaction = false;
            this.notify();
        });

        return this;
    }

    private notify() {
        this.compactChanges();

        for (const { model, change, ignore, source } of this.changeItems) {
            if (ignore) {
                continue;
            }

            // table 数据变更或者继承变更的时候携带 tableChanges 通知
            if (
                (change.type === ChangeType.DataPatch || change.type === ChangeType.PropsInheritance) &&
                model.type === DataType.GraphicalObjectFrameTable
            ) {
                change.diffs.tableChanges = (model as TableDataModel).tableChanges;
                (model as TableDataModel).clearTableChanges();
            }

            model.notify({ ...change, source });
        }

        this.reset();
    }

    // eslint-disable-next-line complexity
    private compactChanges() {
        const childrenChanges = new Set<string>();
        const propsChanges = new Map<string, ChangeItem>();

        for (const changeItem of this.changeItems) {
            const { model, key, change } = changeItem;
            const { type } = change;
            const isDetachedInTransaction = this.detachedModelIdsSet.has(model.id);

            if (isDetachedInTransaction) {
                model.dispose();
            }

            if (
                // 在当前事务中新创建的 Model，不需要通知
                this.freshModelIdsSet.has(model.id) ||
                // 在当前事务中销毁的 Model，不需要通知
                isDetachedInTransaction ||
                // 在当前事务中的列表变更，仅通知一次
                childrenChanges.has(key)
            ) {
                changeItem.ignore = true;
                continue;
            }

            // 属性或自身数据变更，需要合并数据，仅通知一次
            // FIXME: props 的合并基于 modelId
            // TODO: 数组操作未合并
            if (propsChanges.has(key)) {
                changeItem.ignore = true;
                this.mergeChangeData(changeItem, propsChanges);
                continue;
            }

            if (type === ChangeType.ChildList || type === ChangeType.ChildMap) {
                childrenChanges.add(key);
                continue;
            }

            if (type === ChangeType.DataPatch) {
                const originDiffs = {};
                change.diffs.data = protoutil.mergeDiff(originDiffs, change.diffs.data);
                propsChanges.set(key, changeItem);
                continue;
            }

            if (type === ChangeType.PropsInheritance) {
                propsChanges.set(key, changeItem);
            }
        }
    }

    private mergeChangeData(changeItem: ChangeItem, propsChanges: Map<string, ChangeItem>) {
        const { key } = changeItem;
        const lastChange = propsChanges.get(key);

        if (lastChange?.change.type === ChangeType.DataPatch) {
            // 两个 dataPatch 之间需要数据合并
            if (changeItem.change.type === ChangeType.DataPatch) {
                lastChange.change.diffs.data = protoutil.mergeDiff(
                    lastChange.change.diffs.data,
                    changeItem.change.diffs.data,
                );
            }
            // PropsInheritance 是全量的，数据合并直接覆盖即可
            if (changeItem.change.type === ChangeType.PropsInheritance) {
                lastChange.change = changeItem.change;
            }
        }
    }

    private enqueueChange(changeItem: ChangeItem) {
        this.changeItems.push(changeItem);
    }

    private reset() {
        this.freshModelIdsSet.clear();
        this.detachedModelIdsSet.clear();
        this.changeItems = [];
    }

    private getChangeKey(
        model: IObservableDataModel<unknown, unknown> & WithDataModelChangeNotifier<unknown, unknown>,
        change: RawDataModelChange<unknown, unknown>,
        source: MutationSource,
    ): string {
        const baseKey = `${source}:${model.id}`;
        if (change.type === ChangeType.ChildList) {
            return `${baseKey}:${change.diffs.childList.key}`;
        }

        if (change.type === ChangeType.ChildMap) {
            return `${baseKey}:${change.diffs.childMap.key}`;
        }

        return baseKey;
    }
}
